import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getHomeSettings,
  getMyNotes,
  patchHomeSettings,
  updateCroppedPhoto,
  uploadPhoto,
} from "./fetch";
import { memberPortalKeys } from "./keys";

export const useGetHomeSettings = () => {
  return useQuery({
    queryKey: memberPortalKeys.homeSettings(),
    queryFn: getHomeSettings,
  });
};

export const usePatchHomeSettings = ({
  onError,
  onSuccess,
  skipOptimisticUpdate = false,
} = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formState) => {
      return await patchHomeSettings(formState);
    },
    onMutate: async (newData) => {
      if (skipOptimisticUpdate) return;
      await queryClient.cancelQueries({
        queryKey: memberPortalKeys.homeSettings(),
      });

      const previousData = queryClient.getQueryData(
        memberPortalKeys.homeSettings()
      );

      // Optimistically update to the new value
      queryClient.setQueryData(memberPortalKeys.homeSettings(), (old) => ({
        ...old,
        ...newData,
      }));

      return { previousData };
    },
    onError: (err, newData, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(
        memberPortalKeys.homeSettings(),
        context?.previousData
      );

      if (onError) onError(err);
      else console.error(err);
    },
    onSuccess: async (res, variables, context) => {
      // Invalidate and refetch to ensure we have the latest data
      await queryClient.invalidateQueries({
        queryKey: memberPortalKeys.all(),
      });
      if (onSuccess) await onSuccess(res, variables, context);
    },
  });
};

export const useUpdateCroppedPhoto = ({ onError, onSuccess } = {}) => {
  return useMutation({
    mutationFn: async ({ croppedPhotoID, settings }) => {
      return await updateCroppedPhoto(croppedPhotoID, settings);
    },
    onError: (err, newData, context) => {
      if (onError) onError(err);
      else console.error(err);
    },
    onSuccess: async (res, variables, context) => {
      if (onSuccess) await onSuccess(res, variables, context);
    },
  });
};

export const useUploadPhoto = ({ onSuccess, onError }) => {
  return useMutation({
    mutationFn: async (formData, options) => {
      return await uploadPhoto(formData, options);
    },
    onSuccess: (res) => {
      onSuccess && onSuccess(res);
    },
    onError: (err) => {
      if (onError) onError(err);
    },
  });
};

export const useGetNotesInfinite = ({ searchTerm, getNextPageParam }) => {
  const isValidSearchTerm = !searchTerm || searchTerm.length > 2;
  const useableSearchTerm = isValidSearchTerm ? searchTerm : "";

  return useInfiniteQuery({
    queryKey: memberPortalKeys.myNotes(searchTerm),
    queryFn: ({ pageParam: nextPageCursor }) =>
      getMyNotes(nextPageCursor, useableSearchTerm),
    getNextPageParam,
    onError: (err) => {
      console.error("Error fetching notes:", err);
    },
    refetchOnWindowFocus: false,
  });
};
